import { __, setLocaleData } from '@wordpress/i18n';
import keyCodes from 'a11y/lib/keyCodes';
import uniqueId from 'a11y/lib/uniqueId';
import interactiveChildren from 'a11y/lib/interactiveChildren';
import createScreenReaderText from 'a11y/lib/createScreenReaderText';

/**
 * Class for adding listbox-like support to Topic MegaMenu article lists.
 *
 * @param {HTMLElement} articles The articles <ul>
 */
export default class GroupWidget {
  /**
   * Get the article item from one of its interative children.
   *
   * @param {HTMLElement} itemChild The lost child.
   *
   * @return {HTMLElement}
   */
  static getItemParent(itemChild) {
    const { childOf } = itemChild.dataset;
    const itemParent = document.getElementById(childOf);

    return (null !== itemParent) ? itemParent : false;
  }

  /**
   * Start the component
   *
   * @todo can i get a reference to the parent popup?
   */
  constructor(list) {
    this.list = list;
    this.articleItems = Array.prototype.slice.call(list.children);

    // Register text domain
    setLocaleData({ '': {} }, 'sciencenews');

    // Save a reference to the first list item.
    Object.assign(this, { firstItem: this.articleItems[0] });

    /**
     * The index of the last item.
     * @type {Number}
     */
    this.lastIndex = (this.articleItems.length - 1);

    /**
     * Track GroupWidget state.
     * @type {Object}
     */
    this.state = {
      activeDescendant: this.firstItem,
    };

    // Bind class methods.
    this.prepItems = this.prepItems.bind(this);
    this.updateActiveState = this.updateActiveState.bind(this);
    this.clearActiveState = this.clearActiveState.bind(this);
    this.getParentPopup = this.getParentPopup.bind(this);
    this.handleListBlur = this.handleListBlur.bind(this);
    this.handleListKeydown = this.handleListKeydown.bind(this);

    /**
     * The instance the Popup class within which the list resides.
     * @type {Popup}
     */
    this.parentPopup = this.getParentPopup();

    this.prepItems();

    this.init();
  }

  /**
   * Get a reference to the Popup containing the list.
   *
   * @return {Popup} An instance of the Popup class.
   */
  getParentPopup() {
    let parent = this.list.parentElement;

    while (('[object Popup]' !== parent?.popup?.toString())) {
      parent = parent.parentElement;
    }

    return parent.popup;
  }

  /**
   * Ensure each list item and its interactive children have an ID attribute.
   */
  prepItems() {
    this.articleItems.forEach((item) => {
      const children = interactiveChildren(item);

      if ('' === item.id) {
        item.setAttribute('id', uniqueId());
      }

      children.forEach((child) => {
        child.setAttribute('id', uniqueId());
        child.setAttribute('data-child-of', item.id);
      });
    });
  }

  /**
   * Update the active descendant.
   *
   * @param  {HTMLElement} newlyActive The descendant to set as active and selected.
   */
  updateActiveState(newlyActive) {
    const { activeDescendant } = this.state;
    activeDescendant.removeAttribute('aria-selected');

    this.state.activeDescendant = newlyActive;
    this.list.setAttribute('aria-activedescendant', newlyActive.id);
    newlyActive.setAttribute('aria-selected', 'true');
  }

  /**
   * Clear the list attributes and reset state.
   */
  clearActiveState() {
    const { activeDescendant } = this.state;

    this.list.removeAttribute('aria-activedescendant');
    activeDescendant.removeAttribute('aria-selected');
    this.state.activeDescendant = this.firstItem;

    // Revert to the intial help text.
    this.list.setAttribute(
      'aria-describedby',
      this.initialListDescribedby,
    );
  }

  /**
   * Adds screen reader help text for use in aria-describedby attributes.
   */
  static addHelpText() {
    const helpTextItems = [
      {
        id: 'group-widget-list-help',
        text: __(
          'Use up and down arrow keys to explore.',
          'sciencenews',
        ),
      },
      {
        id: 'group-widget-list-enter-help',
        text: __('Use right arrow key to move into the list.', 'sciencenews'),
      },
      {
        id: 'group-widget-move-back-help',
        text: __(
          'Use left arrow key to move back to the parent list.',
          'sciencenews',
        ),
      },
      {
        id: 'group-widget-tab-help',
        text: __('Use tab key to enter the current list item.', 'sciencenews'),
      },
      {
        id: 'group-widget-esc-help',
        text: __(
          'Use escape to exit the menu.',
          'sciencenews',
        ),
      },
    ];

    helpTextItems.forEach(({ id, text }) => {
      if (null === document.body.querySelector(`#${id}`)) {
        const helpSpan = createScreenReaderText(id, text);
        document.body.appendChild(helpSpan);
      }
    });
  }

  init() {
    this.list.setAttribute('role', 'group');
    this.list.setAttribute('tabindex', '0');

    // eslint-disable-next-line max-len
    this.initialListDescribedby = 'group-widget-list-help group-widget-list-enter-help group-widget-esc-help';
    // eslint-disable-next-line max-len
    this.activeListDescribedby = 'group-widget-list-help group-widget-move-back-help group-widget-tab-help group-widget-esc-help';

    // Add the help
    this.constructor.addHelpText();

    // Set the initial list help text.
    this.list.setAttribute(
      'aria-describedby',
      this.initialListDescribedby,
    );

    this.list.addEventListener('blur', this.handleListBlur);
    this.list.addEventListener('keydown', this.handleListKeydown);
  }

  /**
   * Handle list blur to ensure attributes are not present when the list isn't focused.
   */
  handleListBlur() {
    // Revert `focus` handler setup if the activeElement is not contained within the GroupWidget
    if (! this.list.contains(document.activeElement)) {
      this.clearActiveState();
    }
  }

  /**
   * Handle keydown events on the list to ensure the correct element is set as the active descendant.
   *
   * @param  {Object} event The event object.
   */
  handleListKeydown(event) {
    const {
      UP,
      DOWN,
      RIGHT,
      LEFT,
      TAB,
    } = keyCodes;
    const { keyCode, shiftKey } = event;
    const { activeDescendant } = this.state;
    const activeIndex = this.articleItems.indexOf(activeDescendant);
    // The list is considered active once the user has taken action to
    // drill down into the list items, currently by pressing the right arrow key.
    const listIsActive = this.list.hasAttribute('aria-activedescendant');
    // Get the interactive children of the current item, or the parent item
    // if we're on one of the child elements.
    const parentItem = this.constructor.getItemParent(activeDescendant);

    // Mimic the normal menu items' keybopard handling when the list isn't active.
    if (! listIsActive) {
      if (RIGHT === keyCode) {
        // Move into list.
        event.preventDefault();
        this.updateActiveState(this.firstItem);

        // Update the help text to reflect the new state.
        this.list.setAttribute(
          'aria-describedby',
          this.activeListDescribedby,
        );
      } else if (LEFT === keyCode) {
        // Move back to the popup controller.
        event.preventDefault();
        this.parentPopup.setExpandedState(false);
        this.parentPopup.controller.focus();
      } else if ([UP, DOWN].includes(keyCode)) {
        // Move to the sibling list when the list is not active.
        event.preventDefault();
        this.parentPopup.firstItem.focus();
      }
    }

    // Broken out into its own block for readability.
    // Interact with the list items when the list is active.
    if (listIsActive) {
      if (LEFT === keyCode) {
        event.preventDefault();

        if (parentItem) {
          // Move from the child of a list item to the list item
          this.updateActiveState(parentItem);
        } else {
          // Move out of list.
          this.clearActiveState();
        }
      } else if ([UP, DOWN].includes(keyCode) && ! parentItem) {
        // Move up/down through the articles list if the list is active and the
        // active descendant is not a grandchild of the list.
        event.preventDefault();
        let nextIndex = (keyCode === UP)
          ? activeIndex - 1
          : activeIndex + 1;

        // Move to the end if we're moving to the previous child from the first child.
        if (UP === keyCode && 0 > nextIndex) {
          nextIndex = this.lastIndex;
        }

        // Move to first child if we're at the end.
        if (DOWN === keyCode && this.lastIndex < nextIndex) {
          nextIndex = 0;
        }

        this.updateActiveState(this.articleItems[nextIndex]);
      } else if (TAB === keyCode) {
        const children = (parentItem)
          ? interactiveChildren(parentItem)
          : interactiveChildren(activeDescendant);

        if (0 < children.length) {
          event.preventDefault();
          const activeChildIndex = children.indexOf(activeDescendant);
          const [firstChild] = children;

          if (parentItem) {
            // Move up/down through the articles list if the list is active.
            const nextIndex = (shiftKey)
              ? activeChildIndex - 1
              : activeChildIndex + 1;

            // Move back from first child to article item.
            // Move from active descendant's last interactive child back to article item.
            const nextElement = (
              0 > nextIndex || (children.length - 1) < nextIndex
            ) ? parentItem : children[nextIndex];

            this.updateActiveState(nextElement);
          } else if (! shiftKey) {
            // Tab from item to the first child element.
            this.updateActiveState(firstChild);
          }
        }
      }
    }
  }
}
